import type { Script } from '../../../script/domain/models/script.ts'
import useTracksStore, {
  trackSelector,
  TrackType,
  VOICE_TRACK_ID,
} from '../../../../shared/store/tracks.store.tsx'
import { getAudioBufferFromAudioUrl } from '@/utils/get-audio-buffer-from-audio-url.ts'
import { refreshAudioMix } from '@/shared/store/use-cases/refresh-audio-mix.ts'
import type { Voice } from '../../../voice/domain/models/voice.ts'
import useMediaStore from '../../../../shared/store/media.store.tsx'
import { useHistoryStore } from '@/shared/store/history.store.tsx'
import { calculateScriptDuration } from '@/utils/calculate-script-duration.ts'
import type { Clip } from '@/features/clips/domain/models/clip.ts'
import { createTTSSpeech } from '@/features/speech/domain/use-cases/create-tts-speech.ts'

export const addVoiceClip = async (
  voice: Voice,
  script: Script,
  speed = 1,
  expressive = false
): Promise<void> => {
  const { addClip, updateClip } = useTracksStore.getState()
  const track = trackSelector(VOICE_TRACK_ID)(useTracksStore.getState())
  const { addMedia } = useMediaStore.getState()
  const mediaId = crypto.randomUUID()
  const voiceClipId = crypto.randomUUID()
  const lastAvailablePosition = track.clips.reduce((acc, clip) => {
    if (clip.startAt === undefined) return acc
    const clipEnding = clip.startAt + clip.duration + clip.endOffset
    if (clipEnding > acc) {
      return clipEnding
    }
    return acc
  }, 0)

  addMedia(mediaId, undefined)
  const newClip: Clip = {
    id: voiceClipId,
    speechType: 'tts',
    type: TrackType.Voices,
    trackId: VOICE_TRACK_ID,
    name: voice.name,
    startAt: lastAvailablePosition,
    startOffset: 0,
    endOffset: 0,
    duration: calculateScriptDuration(script.text),
    fadeIn: 0,
    fadeOut: 0,
    mediaId,
  }

  addClip(newClip.trackId, newClip)

  useHistoryStore.getState().saveSnapshot()
  useTracksStore.getState().setGeneratedMix(undefined)

  const { speechId, speechUrl } = await createTTSSpeech(
    script.id,
    voice,
    speed,
    expressive
  )

  const speechBuffer = await getAudioBufferFromAudioUrl(speechUrl)

  addMedia(speechId, speechBuffer)
  updateClip(voiceClipId, {
    duration: speechBuffer.duration,
    mediaId: speechId,
    src: speechUrl,
  })

  useHistoryStore.getState().saveSnapshot() // save the clip to history with the new url

  void refreshAudioMix(false)
}
